import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import TopBar from '../layout/TopBar';
import { useHGS } from '../context/HGSContext';
import { computeResidualScore, bandFor, RISK_CATEGORIES, OPERATING_HEAD_IDS } from '../data/riskUniverse';
import { ORG_TREE, lineageForDept, resolveDeptId } from '../data/orgTree';
import {
  ShieldAlert, FileSignature, ClipboardCheck, Activity, AlertTriangle,
  CheckCircle2, Clock, TrendingUp, BarChart3, Layers, ArrowRight, Compass,
  Flame, Users, ChevronRight,
} from 'lucide-react';

/**
 * GRC Dashboard — Admin / Risk Management Secretariat home screen.
 *
 * Aggregates governance KPIs, compliance analytics, escalation tracking,
 * and risk analytics across the full HGS stack. Read-only by design;
 * every tile deep-links to the relevant module.
 */
export default function GrcDashboardPage() {
  const navigate = useNavigate();
  const { riskUniverse, evaluations, applicability, masters, formResponses, pendingApprovals, countermeasures, riskPublished, user } = useHGS();

  const isAdmin = user?.role === 'admin';

  // ── Risk analytics ────────────────────────────────────────────────────
  const riskAnalytics = useMemo(() => {
    const total = riskUniverse.length;
    const enriched = riskUniverse.map((r) => {
      const { residual } = computeResidualScore(r);
      return { ...r, residual, band: bandFor(residual) };
    });
    const high = enriched.filter((r) => r.band === 'High').length;
    const medium = enriched.filter((r) => r.band === 'Medium').length;
    const confirmed = riskUniverse.filter((r) => r.confirmed).length;
    const byCategory = {};
    enriched.forEach((r) => {
      byCategory[r.category] = byCategory[r.category] || { total: 0, high: 0, medium: 0, confirmed: 0 };
      byCategory[r.category].total++;
      if (r.band === 'High') byCategory[r.category].high++;
      if (r.band === 'Medium') byCategory[r.category].medium++;
      if (r.confirmed) byCategory[r.category].confirmed++;
    });
    // Heatmap matrix: classification × OH band (worst-case residual)
    const heatmap = {};
    enriched.forEach((r) => {
      heatmap[r.category] = heatmap[r.category] || {};
      OPERATING_HEAD_IDS.forEach((ohId) => {
        const ev = evaluations?.[r.id]?.[ohId];
        if (!ev) { heatmap[r.category][ohId] = heatmap[r.category][ohId] || null; return; }
        const { residual } = computeResidualScore(ev);
        const band = bandFor(residual);
        const order = { High: 4, Medium: 3, Watch: 2, Low: 1 };
        const prev = heatmap[r.category][ohId];
        if (!prev || order[band] > order[prev]) heatmap[r.category][ohId] = band;
      });
    });
    return { total, high, medium, confirmed, byCategory, heatmap, enriched };
  }, [riskUniverse, evaluations]);

  // ── Compliance analytics ──────────────────────────────────────────────
  const compliance = useMemo(() => {
    const responseStats = (moduleKey) => {
      const rows = masters?.[moduleKey] || [];
      const activeRows = rows.filter((r) => r.active !== false);
      const total = activeRows.length;
      let answered = 0;
      let withEvidence = 0;
      let nonCompliant = 0;
      activeRows.forEach((r) => {
        const idField = moduleKey === 'fraud' ? 'controlId' : 'questionId';
        const responseKey = `${moduleKey}:${r[idField]}`;
        const resp = formResponses?.[responseKey];
        if (!resp) return;
        answered++;
        // Evidence file uploaded
        const evidenceVal = resp.evidenceFile || resp.evidenceAttachment;
        if (evidenceVal && evidenceVal !== '') withEvidence++;
        const primary = resp.compliantYesNo || resp.responseDLC || resp.responseFraud || resp.response || resp.primaryResponse;
        if (primary === 'No' || primary === 'N/G' || primary === 'N/A') nonCompliant++;
      });
      const completionPct = total === 0 ? 0 : Math.round((answered / total) * 100);
      const evidencePct = answered === 0 ? 0 : Math.round((withEvidence / answered) * 100);
      const compliancePct = answered === 0 ? 0 : Math.round(((answered - nonCompliant) / answered) * 100);
      return { total, answered, withEvidence, nonCompliant, completionPct, evidencePct, compliancePct };
    };
    return {
      pcc: responseStats('pcc'),
      fraud: responseStats('fraud'),
      dlc: responseStats('dlc'),
    };
  }, [masters, formResponses]);

  // ── Escalation tracking ───────────────────────────────────────────────
  const escalation = useMemo(() => {
    const cms = countermeasures || [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const open = cms.filter((c) => c.status !== 'Closed');
    const overdue = open.filter((c) => {
      const due = c.dueDate || c.targetDate;
      if (!due) return false;
      return new Date(due) < today;
    });
    // Top 5 overdue by days-overdue desc (uses pre-computed daysOverdue if available)
    const topOverdue = [...overdue]
      .map((c) => {
        const due = c.dueDate || c.targetDate;
        const computed = due ? Math.floor((today.getTime() - new Date(due).getTime()) / 86400_000) : 0;
        return { ...c, _daysOverdue: c.daysOverdue ?? computed };
      })
      .sort((a, b) => b._daysOverdue - a._daysOverdue)
      .slice(0, 5);
    return {
      openCount: open.length,
      overdueCount: overdue.length,
      closedCount: cms.length - open.length,
      pendingApprovals: (pendingApprovals || []).filter((a) => a.status === 'Submitted').length,
      topOverdue,
    };
  }, [countermeasures, pendingApprovals]);

  // ── Per-OH analytics (Operating Head comparison) ──────────────────────
  const byOh = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return OPERATING_HEAD_IDS.map((ohId) => {
      const oh = ORG_TREE[ohId];
      // Risks assigned to this OH via applicability
      const assigned = riskUniverse.filter((r) => (applicability?.[r.id] || []).includes(ohId));
      let high = 0;
      let medium = 0;
      let confirmed = 0;
      let evaluated = 0;
      assigned.forEach((r) => {
        const ev = evaluations?.[r.id]?.[ohId];
        if (!ev) return;
        evaluated++;
        if (ev.confirmed) confirmed++;
        const { residual } = computeResidualScore(ev);
        const band = bandFor(residual);
        if (band === 'High') high++;
        else if (band === 'Medium') medium++;
      });
      // Departments under this OH → set of deptIds for countermeasure filtering
      const deptIds = new Set();
      Object.values(oh.divisions).forEach((div) => {
        Object.keys(div.departments).forEach((dId) => deptIds.add(dId));
      });
      const cmForOh = (countermeasures || []).filter((c) => {
        const resolved = resolveDeptId(c.department);
        if (deptIds.has(resolved)) return true;
        // Fall back to lineage lookup
        const lineage = lineageForDept(resolved);
        return lineage?.ohId === ohId;
      });
      const openRem = cmForOh.filter((c) => c.status !== 'Closed').length;
      const overdueRem = cmForOh.filter((c) => {
        if (c.status === 'Closed') return false;
        const due = c.dueDate || c.targetDate;
        return due && new Date(due) < today;
      }).length;
      return {
        id: ohId,
        label: oh.label,
        color: oh.color,
        head: oh.head,
        assignedCount: assigned.length,
        high, medium, confirmed, evaluated,
        confirmationPct: assigned.length === 0 ? 0 : Math.round((confirmed / assigned.length) * 100),
        evaluationPct: assigned.length === 0 ? 0 : Math.round((evaluated / assigned.length) * 100),
        openRem,
        overdueRem,
        divisionCount: Object.keys(oh.divisions).length,
        deptCount: deptIds.size,
      };
    });
  }, [riskUniverse, evaluations, applicability, countermeasures]);

  return (
    <div className="flex-1 flex flex-col bg-slate-50 min-h-screen">
      <TopBar
        title="GRC Dashboard"
        subtitle="Risk Management Secretariat · Governance framework owner · Consolidated oversight"
        breadcrumbs={['HGS Architecture', 'GRC Dashboard']}
      />
      <div className="px-6 py-6 space-y-6">
        {/* Identity banner */}
        <div className="rounded-xl bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 text-white p-5 border border-slate-700 shadow-sm">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-lg bg-amber-500/15 backdrop-blur flex items-center justify-center flex-shrink-0">
              <Compass className="w-6 h-6 text-amber-400" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-amber-400">
                GRC Secretariat · Governance Controller
              </div>
              <div className="text-lg font-bold mt-0.5">Enterprise Risk &amp; Governance Oversight</div>
              <div className="text-sm text-slate-300 mt-1 max-w-3xl">
                You own the governance framework, master library, applicability mapping and consolidated GRC review.
                Operating Heads contextualise risks for their operations. Department Heads execute compliance.
                Division Heads review. Final governance review returns here.
              </div>
            </div>
            <div className="hidden md:flex items-center gap-5 text-right">
              <Tile label="Risks" value={riskAnalytics.total} />
              <Tile label="Classifications" value={Object.keys(riskAnalytics.byCategory).length} />
              <Tile label="Operating Heads" value={OPERATING_HEAD_IDS.length} />
            </div>
          </div>
        </div>

        {/* ── Governance KPIs ─────────────────────────────────────────── */}
        <Section title="Governance KPIs" icon={Activity}>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <Kpi label="Total Risks"        value={riskAnalytics.total}        icon={ShieldAlert} accent="#0F172A" />
            <Kpi label="High Risk"          value={riskAnalytics.high}         icon={AlertTriangle} accent="#CC0000" testid="kpi-high" />
            <Kpi label="Medium Risk"        value={riskAnalytics.medium}       icon={TrendingUp} accent="#D97706" />
            <Kpi label="Confirmed"          value={`${riskAnalytics.confirmed}/${riskAnalytics.total}`} icon={CheckCircle2} accent="#059669" testid="kpi-confirmed" />
            <Kpi label="Pending Approvals"  value={escalation.pendingApprovals} icon={Clock} accent="#7C3AED" testid="kpi-approvals" />
            <Kpi label="Overdue Items"      value={escalation.overdueCount}    icon={AlertTriangle} accent="#CC0000" testid="kpi-overdue" />
          </div>
        </Section>

        {/* ── Compliance Analytics ────────────────────────────────────── */}
        <Section title="Compliance Analytics" icon={BarChart3}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <ComplianceCard moduleKey="pcc"   title="Product Compliance Data" stats={compliance.pcc}   icon={ClipboardCheck} accent="#1F2937" onView={() => navigate('/pcc')} />
            <ComplianceCard moduleKey="fraud" title="Fraud Prevention"        stats={compliance.fraud} icon={ShieldAlert}    accent="#0EA5E9" onView={() => navigate('/fraud')} />
            <ComplianceCard moduleKey="dlc"   title="DLC Self-Verification"   stats={compliance.dlc}   icon={FileSignature}  accent="#7C3AED" onView={() => navigate('/dlc')} />
          </div>
        </Section>

        {/* ── Escalation Tracking ─────────────────────────────────────── */}
        <Section title="Escalation Tracking" icon={AlertTriangle}>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <EscalationCard label="Open Remediation"  value={escalation.openCount}     accent="#D97706" onClick={() => navigate('/countermeasures')} testid="esc-open" />
            <EscalationCard label="Overdue Items"     value={escalation.overdueCount}  accent="#CC0000" onClick={() => navigate('/countermeasures')} testid="esc-overdue" />
            <EscalationCard label="Pending Approvals" value={escalation.pendingApprovals} accent="#7C3AED" onClick={() => navigate('/approvals')} testid="esc-approvals" />
            <EscalationCard label="Closed Items"      value={escalation.closedCount}   accent="#059669" onClick={() => navigate('/countermeasures')} />
          </div>
        </Section>

        {/* ── Top Overdue Items ───────────────────────────────────────── */}
        <Section title="Top Overdue Items" icon={Flame}>
          <TopOverdueList
            items={escalation.topOverdue}
            totalOverdue={escalation.overdueCount}
            onOpenAll={() => navigate('/countermeasures')}
          />
        </Section>

        {/* ── Operating Head Comparison ───────────────────────────────── */}
        <Section title="Operating Head Comparison" icon={Users}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="oh-comparison">
            {byOh.map((oh) => (
              <OhComparisonCard
                key={oh.id}
                oh={oh}
                onOpenRisk={() => navigate('/risk')}
                onOpenRemediation={() => navigate('/countermeasures')}
              />
            ))}
          </div>
        </Section>

        {/* ── Risk Analytics — Classification breakdown ───────────────── */}
        <Section title="Risks by Classification" icon={Layers}>
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-2 font-bold uppercase tracking-wider text-[10px] text-slate-600">Classification</th>
                  <th className="text-right px-4 py-2 font-bold uppercase tracking-wider text-[10px] text-slate-600">Total</th>
                  <th className="text-right px-4 py-2 font-bold uppercase tracking-wider text-[10px] text-slate-600">High</th>
                  <th className="text-right px-4 py-2 font-bold uppercase tracking-wider text-[10px] text-slate-600">Medium</th>
                  <th className="text-right px-4 py-2 font-bold uppercase tracking-wider text-[10px] text-slate-600">Confirmed</th>
                  <th className="text-right px-4 py-2 font-bold uppercase tracking-wider text-[10px] text-slate-600">HR & Admin</th>
                  <th className="text-right px-4 py-2 font-bold uppercase tracking-wider text-[10px] text-slate-600">Manufacturing</th>
                </tr>
              </thead>
              <tbody data-testid="risks-by-classification-table">
                {RISK_CATEGORIES.filter((c) => riskAnalytics.byCategory[c]).map((cat) => {
                  const row = riskAnalytics.byCategory[cat];
                  const hm = riskAnalytics.heatmap[cat] || {};
                  return (
                    <tr key={cat} className="border-b border-slate-100 hover:bg-slate-50 transition">
                      <td className="px-4 py-2 font-semibold text-slate-800">{cat}</td>
                      <td className="text-right px-4 py-2 font-mono">{row.total}</td>
                      <td className="text-right px-4 py-2 font-mono"><span className={row.high > 0 ? 'font-bold text-red-700' : 'text-slate-400'}>{row.high}</span></td>
                      <td className="text-right px-4 py-2 font-mono"><span className={row.medium > 0 ? 'font-bold text-amber-700' : 'text-slate-400'}>{row.medium}</span></td>
                      <td className="text-right px-4 py-2 font-mono text-emerald-700">{row.confirmed}/{row.total}</td>
                      <td className="text-right px-4 py-2"><HeatCell band={hm['hr-admin']} /></td>
                      <td className="text-right px-4 py-2"><HeatCell band={hm['manufacturing']} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="text-[10px] text-slate-500 italic mt-2">
            Heatmap colour = worst-case residual band across that classification, per Operating Head. Grey = no OH evaluation yet.
          </div>
        </Section>

        {/* ── Risk Publication Status ─────────────────────────────────── */}
        <Section title="Governance Cycle" icon={Compass}>
          <div className="bg-white rounded-xl border border-slate-200 p-5 flex items-center justify-between">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Risk Universe Status</div>
              {riskPublished?.published ? (
                <>
                  <div className="text-base font-bold text-emerald-700 mt-1 flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4" />
                    Published — {riskPublished.version}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    Published by <b>{riskPublished.publishedBy}</b> · PCC / Fraud / DLC modules are active
                  </div>
                </>
              ) : (
                <>
                  <div className="text-base font-bold text-amber-700 mt-1 flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Awaiting publication
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5">Operating Head must confirm all 45 risks before publishing</div>
                </>
              )}
            </div>
            {isAdmin && (
              <button onClick={() => navigate('/risk')} className="inline-flex items-center gap-1.5 px-3 py-2 bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold rounded-lg transition" data-testid="grc-open-risk">
                Open Risk Universe <ArrowRight className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </Section>
      </div>
    </div>
  );
}

// ─── Sub-components ────────────────────────────────────────────────────
function Section({ title, icon: Icon, children }) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4 text-slate-600" />
        <h2 className="text-sm font-bold uppercase tracking-wider text-slate-700">{title}</h2>
      </div>
      {children}
    </div>
  );
}

function Tile({ label, value }) {
  return (
    <div className="text-right">
      <div className="text-[9px] font-bold uppercase tracking-wider text-slate-400">{label}</div>
      <div className="text-lg font-bold tabular-nums">{value}</div>
    </div>
  );
}

function Kpi({ label, value, icon: Icon, accent, testid }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-3 flex items-start gap-3" data-testid={testid}>
      <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: accent + '15', color: accent }}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[9px] font-bold uppercase tracking-wider text-slate-500">{label}</div>
        <div className="text-lg font-bold tabular-nums text-slate-900 leading-none mt-1">{value}</div>
      </div>
    </div>
  );
}

function ComplianceCard({ moduleKey, title, stats, icon: Icon, accent, onView }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`compliance-${moduleKey}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: accent + '15', color: accent }}>
            <Icon className="w-4 h-4" />
          </div>
          <div className="text-sm font-bold text-slate-800">{title}</div>
        </div>
        <button onClick={onView} className="text-[10px] font-bold uppercase tracking-wider text-slate-500 hover:text-slate-800" data-testid={`compliance-open-${moduleKey}`}>
          Open →
        </button>
      </div>
      <div className="grid grid-cols-3 gap-2 mb-3 text-center">
        <PctBar label="Completion" value={stats.completionPct} accent={accent} />
        <PctBar label="Evidence"   value={stats.evidencePct}   accent={accent} />
        <PctBar label="Compliance" value={stats.compliancePct} accent={accent} />
      </div>
      <div className="text-[10px] text-slate-500 flex items-center justify-between border-t border-slate-100 pt-2">
        <span>Answered <b className="text-slate-800 font-mono">{stats.answered}/{stats.total}</b></span>
        <span>Non-compliant: <b className={stats.nonCompliant ? 'text-red-700 font-mono' : 'text-slate-400 font-mono'}>{stats.nonCompliant}</b></span>
      </div>
    </div>
  );
}

function PctBar({ label, value, accent }) {
  return (
    <div>
      <div className="text-[8px] font-bold uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-base font-bold tabular-nums" style={{ color: accent }}>{value}%</div>
      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mt-1">
        <div className="h-full transition-all" style={{ width: `${Math.min(100, value)}%`, backgroundColor: accent }} />
      </div>
    </div>
  );
}

function EscalationCard({ label, value, accent, onClick, testid }) {
  return (
    <button onClick={onClick} className="bg-white rounded-xl border border-slate-200 p-4 text-left hover:border-slate-300 transition" data-testid={testid}>
      <div className="text-[9px] font-bold uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-2xl font-bold tabular-nums mt-1" style={{ color: accent }}>{value}</div>
      <div className="text-[10px] text-slate-400 mt-2 flex items-center gap-1">View → </div>
    </button>
  );
}

function HeatCell({ band }) {
  const styles = {
    High:   { bg: '#FEE2E2', text: '#991B1B', label: 'High' },
    Medium: { bg: '#FED7AA', text: '#9A3412', label: 'Med' },
    Watch:  { bg: '#FEF08A', text: '#854D0E', label: 'Watch' },
    Low:    { bg: '#BBF7D0', text: '#166534', label: 'Low' },
  };
  if (!band) return <span className="inline-block w-12 h-5 rounded-sm bg-slate-100" title="No evaluation" />;
  const s = styles[band];
  return (
    <span className="inline-block w-12 px-1.5 py-0.5 rounded-sm text-[9px] font-bold uppercase tracking-wider text-center" style={{ backgroundColor: s.bg, color: s.text }}>
      {s.label}
    </span>
  );
}

// ── Operating Head Comparison Card ───────────────────────────────────────
function OhComparisonCard({ oh, onOpenRisk, onOpenRemediation }) {
  const color = oh.color || '#0F172A';
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid={`oh-card-${oh.id}`}>
      {/* Header strip with OH color */}
      <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-3" style={{ backgroundColor: color + '0D' }}>
        <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 text-white font-bold text-xs" style={{ backgroundColor: color }}>
          {oh.head?.avatar || oh.label.slice(0, 2).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold text-slate-900 truncate">{oh.label}</div>
          <div className="text-[10px] text-slate-500 truncate">
            {oh.head?.name} · {oh.divisionCount} divisions · {oh.deptCount} departments
          </div>
        </div>
        <div className="text-right">
          <div className="text-[9px] font-bold uppercase tracking-wider text-slate-500">Risks Assigned</div>
          <div className="text-lg font-bold tabular-nums" style={{ color }}>{oh.assignedCount}</div>
        </div>
      </div>
      {/* Stats grid */}
      <div className="grid grid-cols-4 gap-px bg-slate-100">
        <OhStat label="High" value={oh.high} tone="red" />
        <OhStat label="Medium" value={oh.medium} tone="amber" />
        <OhStat label="Confirmed" value={`${oh.confirmed}/${oh.assignedCount}`} tone="emerald" />
        <OhStat label="Evaluated" value={`${oh.evaluationPct}%`} tone="slate" />
      </div>
      {/* Confirmation progress bar */}
      <div className="px-4 py-3 border-t border-slate-100">
        <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">
          <span>Confirmation Progress</span>
          <span className="tabular-nums" style={{ color }}>{oh.confirmationPct}%</span>
        </div>
        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
          <div className="h-full transition-all" style={{ width: `${oh.confirmationPct}%`, backgroundColor: color }} />
        </div>
      </div>
      {/* Footer actions */}
      <div className="px-4 py-3 border-t border-slate-100 flex items-center justify-between bg-slate-50/60">
        <div className="flex items-center gap-3 text-[11px]">
          <div className="flex items-center gap-1.5">
            <span className="text-slate-500">Open</span>
            <span className={`font-bold tabular-nums ${oh.openRem ? 'text-amber-700' : 'text-slate-400'}`}>{oh.openRem}</span>
          </div>
          <div className="text-slate-300">·</div>
          <div className="flex items-center gap-1.5">
            <span className="text-slate-500">Overdue</span>
            <span className={`font-bold tabular-nums ${oh.overdueRem ? 'text-red-700' : 'text-slate-400'}`}>{oh.overdueRem}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={onOpenRisk} className="text-[10px] font-bold uppercase tracking-wider text-slate-500 hover:text-slate-900 transition" data-testid={`oh-open-risk-${oh.id}`}>
            Risk →
          </button>
          <button onClick={onOpenRemediation} className="text-[10px] font-bold uppercase tracking-wider text-slate-500 hover:text-slate-900 transition" data-testid={`oh-open-rem-${oh.id}`}>
            Remediation →
          </button>
        </div>
      </div>
    </div>
  );
}

function OhStat({ label, value, tone }) {
  const tones = {
    red:     { text: 'text-red-700' },
    amber:   { text: 'text-amber-700' },
    emerald: { text: 'text-emerald-700' },
    slate:   { text: 'text-slate-800' },
  };
  const t = tones[tone] || tones.slate;
  return (
    <div className="bg-white px-3 py-3 text-center">
      <div className="text-[9px] font-bold uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-base font-bold tabular-nums mt-0.5 ${t.text}`}>{value}</div>
    </div>
  );
}

// ── Top Overdue Items List ───────────────────────────────────────────────
function TopOverdueList({ items, totalOverdue, onOpenAll }) {
  if (!items || items.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 px-5 py-6 flex items-center gap-3" data-testid="top-overdue-empty">
        <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
          <CheckCircle2 className="w-4 h-4 text-emerald-600" />
        </div>
        <div>
          <div className="text-sm font-bold text-slate-800">No overdue countermeasures</div>
          <div className="text-[11px] text-slate-500 mt-0.5">Every remediation task is on track or has been closed.</div>
        </div>
      </div>
    );
  }
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="top-overdue-list">
      <div className="divide-y divide-slate-100">
        {items.map((cm) => (
          <OverdueRow key={cm.id} cm={cm} />
        ))}
      </div>
      {totalOverdue > items.length && (
        <button
          onClick={onOpenAll}
          className="w-full px-4 py-2.5 text-[11px] font-bold uppercase tracking-wider text-slate-600 hover:text-slate-900 hover:bg-slate-50 border-t border-slate-100 flex items-center justify-center gap-1 transition"
          data-testid="top-overdue-view-all"
        >
          View all {totalOverdue} overdue items <ChevronRight className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}

function OverdueRow({ cm }) {
  const days = cm._daysOverdue ?? cm.daysOverdue ?? 0;
  const moduleTone = {
    DLC:   { bg: 'bg-violet-50', text: 'text-violet-700' },
    FRAUD: { bg: 'bg-sky-50',    text: 'text-sky-700' },
    PCC:   { bg: 'bg-slate-100', text: 'text-slate-700' },
  }[cm.module] || { bg: 'bg-slate-100', text: 'text-slate-700' };
  const lineage = lineageForDept(resolveDeptId(cm.department));
  const deptLabel = lineage?.deptLabel || cm.department || '—';
  const ohLabel = lineage?.ohLabel;
  return (
    <div className="px-4 py-3 flex items-start gap-3 hover:bg-slate-50 transition" data-testid={`overdue-row-${cm.id}`}>
      <div className="w-10 h-10 rounded-lg bg-red-50 border border-red-100 flex flex-col items-center justify-center flex-shrink-0">
        <div className="text-[8px] font-bold uppercase tracking-wider text-red-600 leading-none">Late</div>
        <div className="text-sm font-bold text-red-700 tabular-nums leading-none mt-0.5">{days}d</div>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider ${moduleTone.bg} ${moduleTone.text}`}>
            {cm.module}
          </span>
          {cm.taskType && (
            <span className="inline-block px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-amber-50 text-amber-800 border border-amber-200">
              {cm.taskType}
            </span>
          )}
          {cm.riskId && (
            <span className="inline-block px-1.5 py-0.5 rounded text-[9px] font-mono font-bold bg-slate-100 text-slate-700">
              {cm.riskId}
            </span>
          )}
          <span className="text-[10px] text-slate-400 font-mono">{cm.relatedId}</span>
        </div>
        <div className="text-sm font-semibold text-slate-800 mt-1 line-clamp-2">
          {cm.issue || 'Remediation pending'}
        </div>
        <div className="text-[10px] text-slate-500 mt-1 flex items-center gap-2 flex-wrap">
          <span>Owner: <b className="text-slate-700">{cm.owner || 'Unassigned'}</b></span>
          <span className="text-slate-300">·</span>
          <span>{deptLabel}{ohLabel ? ` · ${ohLabel}` : ''}</span>
          {cm.dueDate && (
            <>
              <span className="text-slate-300">·</span>
              <span>Due <b className="text-slate-700">{cm.dueDate}</b></span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
